import { useEffect } from "react";
import { useAuthState, useGetIdentity } from "ra-core";
import {
  appearanceKey,
  appearanceAccount,
  usePersistentAppearance,
  migrateLegacyTheme,
} from "./appearance-preferences";

import { ThemeProviderContext, type Theme } from "./theme-context";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};
const themes: readonly Theme[] = ["system", "light", "dark"];

/**
 * Theme provider that enables light, dark, and system theme modes.
 *
 * @internal
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "theme",
  ...props
}: ThemeProviderProps) {
  const { authenticated } = useAuthState();
  const { identity } = useGetIdentity({
    enabled: !!authenticated,
    staleTime: 5 * 60 * 1000,
  });
  const account = appearanceAccount(authenticated ? identity?.id : undefined);
  const [theme, setTheme] = usePersistentAppearance<Theme>(
    appearanceKey(account, storageKey),
    defaultTheme,
    themes,
  );

  useEffect(() => {
    migrateLegacyTheme(account, storageKey);
  }, [account, storageKey]);

  useEffect(() => {
    const root = window.document.documentElement;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      root.classList.remove("light", "dark");
      root.classList.add(
        theme === "system" ? (media.matches ? "dark" : "light") : theme,
      );
    };
    apply();
    if (theme === "system") media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);

  const value = {
    theme,
    setTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
